import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeftRight, FileSignature, CalendarClock, CheckSquare, BarChart3, BookOpen, Check } from "lucide-react";
import { Container, SectionLabel, PrimaryLink, OutlineLink, CtaBand } from "./_ui";
import { Seo } from "@/components/marketing/Seo";

const FEATURES = [
  { icon: ArrowLeftRight, title: "Transaksjoner", desc: "Emisjoner, kjøp og salg av aksjer digitalt — med sluttseddel, signering og full sporbarhet." },
  { icon: FileSignature, title: "Aksjekjøpsavtaler", desc: "Generer avtaler fra maler, send til BankID-signering og arkiver på riktig selskap." },
  { icon: CalendarClock, title: "Styremøter", desc: "Innkalling, saksliste, dokumenter og protokoll i samme flyt." },
  { icon: CheckSquare, title: "Oppgaver", desc: "Vedtak blir til oppgaver med ansvarlig og frist." },
  { icon: BarChart3, title: "Nøkkeltall", desc: "Tall hentes automatisk fra Tripletex, Fiken og PowerOffice." },
  { icon: BookOpen, title: "Aksjeeierbok", desc: "Oppdatert eierfordeling, aksjeklasser og verdiutvikling." },
];

const STATS = [
  { value: "73 mill. kr", label: "omsetning fulgt opp i porteføljer" },
  { value: "100 %", label: "av vedtak med sporbar historikk" },
  { value: "20 min", label: "fra oppstart til første styremøte" },
];

const MOTE_POINTS = [
  "Saksliste og dokumenter samlet per møte",
  "Vedtak blir automatisk til oppgaver med frist",
  "Protokollen arkiveres på riktig selskap",
];

export default function Landing() {
  return (
    <>
      <Seo description="Murphy Styrerom samler transaksjoner, aksjekjøpsavtaler, styremøter, oppgaver og nøkkeltall for alle selskapene dine — i ett digitalt styrerom. 199 kr per måned." path="/" />
      <Container className="pt-16 sm:pt-24">
        <h1 className="mt-6 max-w-4xl font-headings text-5xl font-extrabold leading-[0.98] tracking-tight text-neutral-900 sm:text-7xl">
          Styrearbeid uten at noe går galt.
        </h1>
        <p className="mt-6 max-w-2xl text-lg leading-relaxed text-neutral-600">
          Murphy Styrerom samler transaksjoner, aksjekjøpsavtaler, styremøter, oppgaver og nøkkeltall for alle selskapene dine — i ett digitalt styrerom.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <PrimaryLink to="/priser" testid="hero-kom-i-gang">Kom i gang — 199 kr/mnd <ArrowRight className="h-4 w-4" /></PrimaryLink>
          <OutlineLink to="/funksjoner" testid="hero-se-funksjoner">Se funksjonene</OutlineLink>
        </div>
        <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-neutral-500">
          {["BankID-signering", "Data lagret i Norge", "Integrert AI-assistent"].map((t) => (
            <span key={t} className="inline-flex items-center gap-1.5"><Check className="h-4 w-4 text-neutral-900" /> {t}</span>
          ))}
        </div>
        <div className="mt-14 overflow-hidden rounded-[2rem]">
          <img src="/landing/boardroom.jpg" alt="Styrebord sett ovenfra med dokumenter til styremøte" className="h-[280px] w-full object-cover sm:h-[440px]" />
        </div>
      </Container>

      <Container className="py-14 sm:py-20">
        <div className="grid gap-8 border-y border-neutral-200 py-12 sm:grid-cols-3">
          {STATS.map((s) => (
            <div key={s.label}>
              <p className="font-headings text-4xl font-extrabold tracking-tight text-neutral-900 sm:text-5xl">{s.value}</p>
              <p className="mt-2 text-sm text-neutral-500">{s.label}</p>
            </div>
          ))}
        </div>
      </Container>

      <Container className="py-8 sm:py-12">
        <h2 className="max-w-3xl font-headings text-3xl font-extrabold leading-tight tracking-tight text-neutral-900 sm:text-5xl">
          Noe i innboksen. Noe i regnearket. Resten i hodet til styrelederen.
        </h2>
        <p className="mt-6 max-w-2xl text-lg leading-relaxed text-neutral-600">
          Protokoller på e-post, aksjeeierbok i Excel og nøkkeltall som er tre måneder gamle. Murphy Styrerom samler det som faktisk må henge sammen — eierskap, beslutninger og tall — på ett sted.
        </p>
      </Container>

      <Container className="py-12 sm:py-20">
        <SectionLabel>Funksjoner</SectionLabel>
        <h2 className="mt-4 max-w-3xl font-headings text-3xl font-extrabold tracking-tight text-neutral-900 sm:text-5xl">
          Alt styret trenger, samlet på ett sted.
        </h2>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f) => (
            <div key={f.title} className="rounded-2xl border border-neutral-200 p-7 transition-colors hover:border-neutral-900">
              <div className="grid h-11 w-11 place-items-center rounded-xl bg-[#CDF546]"><f.icon className="h-5 w-5 text-neutral-900" strokeWidth={1.9} /></div>
              <h3 className="mt-5 font-headings text-lg font-bold text-neutral-900">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-neutral-600">{f.desc}</p>
            </div>
          ))}
        </div>
        <div className="mt-10">
          <Link to="/funksjoner" data-testid="landing-utforsk" className="inline-flex items-center gap-2 text-sm font-semibold text-neutral-900 hover:underline">
            Utforsk all funksjonalitet <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </Container>

      <Container className="py-12 sm:py-20">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div className="overflow-hidden rounded-[2rem]">
            <img src="/landing/styremote.jpg" alt="Styremedlemmer rundt et møtebord med laptoper og saksliste" className="h-[320px] w-full object-cover sm:h-[420px]" />
          </div>
          <div>
            <SectionLabel>Møter og vedtak</SectionLabel>
            <h2 className="mt-4 font-headings text-3xl font-extrabold tracking-tight text-neutral-900 sm:text-4xl">
              Fra innkalling til signert protokoll.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-neutral-600">
              Sett opp møtet, bygg sakslisten fra maler og del dokumentene med styret. Under møtet føres vedtakene fortløpende, og protokollen signeres med BankID før dere forlater rommet.
            </p>
            <ul className="mt-6 space-y-3">
              {MOTE_POINTS.map((p) => (
                <li key={p} className="flex items-start gap-3 text-sm text-neutral-700">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-neutral-900" /> {p}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Container>

      <Container className="py-12 sm:py-16">
        <figure className="max-w-3xl">
          <blockquote className="font-headings text-2xl font-bold leading-snug tracking-tight text-neutral-900 sm:text-3xl">
            «Vi bruker styrerommet på alle porteføljeselskapene. Endelig ligger avtaler, aksjeeierbok og tall på samme sted når styret skal ta en beslutning.»
          </blockquote>
          <figcaption className="mt-5 text-sm text-neutral-500">Styreleder · investeringsselskap med 11 datterselskaper</figcaption>
        </figure>
      </Container>

      <CtaBand />
    </>
  );
}
